<template>
	<Dialog
		:dialog-visible="dialogVisible"
		:title="'Send Feedback'"
		:agree-text="isSubmitting ? 'Sending...' : 'Send'"
		:cancel-text="'Cancel'"
		:disable-agree-button="isSubmitting"
		:options="{ width: '500px' }"
		@cancel="handleCancel"
		@agree="handleSend">
		<template #dialog-body>
			<!-- Feedback Value Banner -->
			<div class="mb-3">Your feedback helps us improve. Thank you for helping us create a better experience.</div>
			<v-form ref="feedbackForm" v-model="isFormValid">
				<CharacterLimitedTextArea
					v-model="feedbackText"
					field-noun="feedback"
					placeholder="Share your thoughts, suggestions, or any issues..."
					variant="outlined"
					rows="6"
					:max-length="FEEDBACK_MAX_LENGTH"
					:rules="feedbackRules"
					validate-on="invalid-input"
					enterkeyhint="enter"
					class="feedback-textarea"
					@keydown.enter.stop>
					<template #label><FieldLabel label="Your feedback (Required)" /></template>
				</CharacterLimitedTextArea>
			</v-form>
		</template>
	</Dialog>
</template>

<script>
	import Dialog from "./Dialog.vue";
	import api from "../api";
	import { VuetifyValidation } from "@/helpers/Validation";
	import { calculateTimeout } from "@/helpers/utilities";

	// LSP-16853: FEEDBACK_MAX_LENGTH is not a display limit tied to a server column — /me/feedback
	// (contact-api routes/me.ts:468, controllers/users.ts:1379-1382) checks only for a non-empty
	// string and emails the text to support; it enforces no length of its own. So there is no
	// server-unit backstop to add here (§3.3 arithmetic): CharacterLimitedTextArea's own code-point
	// :max-length rule is the only limit that applies.
	const FEEDBACK_MAX_LENGTH = 2000;
	import { useToast } from "vue-toastification";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";

	export default {
		name: "CustomerFeedbackDialog",
		components: {
			FieldLabel,
			Dialog
		},
		setup() {
			const toast = useToast();
			return { toast };
		},
		props: {
			/**
			 * Controls the visibility of the feedback dialog
			 */
			dialogVisible: {
				type: Boolean,
				default: false
			}
		},
		data() {
			return {
				/**
				 * The feedback text entered by the user
				 */
				feedbackText: "",
				/**
				 * Tracks if the form is currently valid
				 */
				isFormValid: false,
				/**
				 * Tracks if the feedback is being submitted
				 */
				isSubmitting: false
			};
		},
		computed: {
			FEEDBACK_MAX_LENGTH: () => FEEDBACK_MAX_LENGTH,
			/**
			 * Validation rules for the feedback text field. The length limit itself is no longer
			 * listed here — CharacterLimitedTextArea's own :max-length rule (code points) covers it;
			 * see the FEEDBACK_MAX_LENGTH comment above for why no server-unit backstop is needed.
			 */
			feedbackRules() {
				return [
					// Feedback is required (LSP-16740): the field is no longer labeled
					// "(Optional)". Empty / whitespace-only input fails this rule, turning
					// the field red and blocking submit via handleSend's validate() gate.
					VuetifyValidation.RequiredValidationField("Please enter your feedback before sending.").isValid
				];
			}
		},
		watch: {
			/**
			 * Reset form when dialog becomes visible
			 */
			dialogVisible(newValue) {
				if (newValue) {
					// Use nextTick to ensure the dialog is fully rendered before resetting
					this.$nextTick(() => {
						this.resetForm();
					});
				}
			}
		},
		methods: {
			/**
			 * Handles the cancel action - closes the dialog and resets the form
			 */
			handleCancel() {
				this.resetForm();
				this.$emit("cancel");
			},

			/**
			 * Handles the send action - submits the feedback if valid
			 */
			async handleSend() {
				// Validate the form before submitting
				const result = await this.$refs.feedbackForm?.validate();
				if (!result?.valid) {
					return;
				}

				this.isSubmitting = true;

				try {
					// Submit feedback to the API
					await api.post("/me/feedback", {
						text: this.feedbackText.trim()
					});

					// Emit success event and close dialog
					this.$emit("feedbackSent");
					this.resetForm();

					// Show thank you message
					const message = "Thank you for your feedback! We'll review it and get back to you if needed.";
					this.toast?.success(message, {
						timeout: calculateTimeout(message)
					});
				} catch (error) {
					console.error(error, { extra: { feedbackText: this.feedbackText }, tags: { type: "feedback" } });

					// Show error message
					const errorMessage = error?.message || "Failed to submit feedback. Please try again.";
					this.toast?.error(errorMessage, {
						timeout: calculateTimeout(errorMessage)
					});
				} finally {
					this.isSubmitting = false;
				}
			},

			/**
			 * Resets the form to its initial state
			 */
			resetForm() {
				this.isSubmitting = false;
				if (this.$refs.feedbackForm) {
					this.$refs.feedbackForm.reset();
					this.feedbackText = "";
				}
			}
		}
	};
</script>
